import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CustomerDataChangeStateService } from '../customer/change/customer-data-change-state.service';
import { InvoiceDataChangeStateService } from '../invoices/invoice-data/change/invoice-data-change-state.service';
import { OrderService } from '../order/order.service';
import { TariffChangeService } from '../tariff/tariff-change.service';
import { AbstractTariffChangeLogic } from '../tariff/abstract-tariff-change.logic';
import { VoucherRedeemStateService } from '../voucher/redeem/voucher-redeem-state.service';
import {
  CHARGING_CARD_ORDER_ROUTE,
  CHARGING_CARD_ORDER_STATUS_ROUTE,
  CLEARING_ACCOUNT_INFORMATION_REQUEST_ROUTE,
  CLEARING_ACCOUNT_INFORMATION_REQUEST_STATUS_ROUTE,
  CUSTOMER_DATA_CHANGE_ROUTE,
  CUSTOMER_DATA_CHANGE_STATUS_ROUTE,
  EMOB_CANCELLATION_ROUTE,
  EMOB_CANCELLATION_STATUS_ROUTE,
  FeatureToggleEnum,
  FIND_PAYMENT_SOLUTION_ROUTE,
  FIND_PAYMENT_SOLUTION_STATUS_ROUTE,
  INTERNET_ROUTE,
  INVOICE_ADDRESS_CHANGE_ROUTE,
  INVOICE_ADDRESS_CHANGE_STATUS_ROUTE,
  INVOICE_PARTIALPAYMENT_CHANGE_RATE_ROUTE,
  INVOICE_PARTIALPAYMENT_CHANGE_STATUS_ROUTE,
  MOVE_OUT_ROUTE,
  MOVE_OUT_STATUS_ROUTE,
  NEW_CLIENT_ROUTE,
  NEW_CLIENT_STATUS_ROUTE,
  OFFER_CONFIRMATION_ROUTE,
  OFFER_CONFIRMATION_STATUS_ROUTE,
  PDF_INVOICE_CHANGE_ROUTE,
  PDF_INVOICE_CHANGE_STATUS_ROUTE,
  RELOCATION_ROUTE,
  RELOCATION_STATUS_ROUTE,
  SECURE_OFFER_ROUTE,
  SECURE_OFFER_STATUS_ROUTE,
  SEPA_INVOICE_CHANGE_ROUTE,
  SEPA_INVOICE_CHANGE_STATUS_ROUTE,
  SUPERSCHNELL_ROUTE,
  TARIFF_CHANGE_EMOBILITY_ROUTE,
  TARIFF_CHANGE_ROUTE,
  TARIFF_CHANGE_STATUS_ROUTE,
  VOUCHER_REDEEM_ROUTE,
  VOUCHER_REDEEM_STATUS_ROUTE,
} from '@mwe/constants';
import { DataChangeType, RecentOrder, RecentOrderType } from '@mwe/models';
import { FindPaymentSolutionStateService } from '../invoices/find-payment-solution/find-payment-solution-state.service';
import { PartialPaymentStateService } from '../partial-payment/partial-payment-state.service';
import { CAInformationRequestStateService } from '../invoices/clearing-account/ca-information-request-state.service';
import { MoveOutStateService } from '../move-out/move-out-state.service';
import { RelocationStore } from '../relocation/relocation.store';
import { InternetStore } from '../order-process/internet/internet.store';
import { ProfileService } from '../profile/profile.service';
import { ChargingCardOrderStore } from '../order-process/charging-card/charging-card-order.store';
import { EmobCancellationStore } from '../emob-cancellation/emob-cancellation.store';
import { TariffChangeEmobilityStore } from '../tariff/tariff-change-emobility.store';

@Injectable({ providedIn: 'root' })
export class RecentOrderLogic {
  private readonly router = inject(Router);
  private readonly invoiceDataChangeStateService = inject(InvoiceDataChangeStateService);
  private readonly tariffChangeService = inject(TariffChangeService);
  private readonly tariffChangeLogic = inject(AbstractTariffChangeLogic);
  private readonly orderService = inject(OrderService);
  private readonly customerDataChangeStateService = inject(CustomerDataChangeStateService);
  private readonly voucherRedeemStateService = inject(VoucherRedeemStateService);
  private readonly chargingCardOrderStore = inject(ChargingCardOrderStore);
  private readonly findPaymentSolutionStateService = inject(FindPaymentSolutionStateService);
  private readonly partialPaymentStateService = inject(PartialPaymentStateService);
  private readonly caInformationRequestStateService = inject(CAInformationRequestStateService);
  private readonly moveOutStateService = inject(MoveOutStateService);
  private readonly profileService = inject(ProfileService);
  private readonly relocationStore = inject(RelocationStore);
  private readonly internetStore = inject(InternetStore);
  private readonly emobCancellationStore = inject(EmobCancellationStore);
  private readonly emobTariffChangeStore = inject(TariffChangeEmobilityStore);

  async navigateToOrderDetails(order: RecentOrder) {
    const idStr = `${order.id}`;
    const idNumber = Number(order.id);
    const dataTypeLower = order.dataType?.toLowerCase() ?? '';

    switch (order.type) {
      case RecentOrderType.InvoiceDataChange:
        this.invoiceDataChangeStateService.lastOrderId = idStr;
        await this.navigateToInvoiceDataChangeStatus(order.dataType);
        break;
      case RecentOrderType.TariffChange:
        await this.handleTariffChange(order, idStr, idNumber, dataTypeLower);
        break;
      // TODO: Once INTERNET_V2_ENABLED is removed, split this up
      case RecentOrderType.NewClient:
      case RecentOrderType.InternetOrder:
        await this.handleNewClient(order, idStr);
        break;
      case RecentOrderType.CustomerChange:
        this.customerDataChangeStateService.lastOrderId = idStr;
        await this.navigateToStatusRoute(CUSTOMER_DATA_CHANGE_ROUTE, CUSTOMER_DATA_CHANGE_STATUS_ROUTE);
        break;
      case RecentOrderType.VoucherRedeem:
        this.voucherRedeemStateService.lastOrderId = idStr;
        await this.navigateToStatusRoute(VOUCHER_REDEEM_ROUTE, VOUCHER_REDEEM_STATUS_ROUTE);
        break;
      case RecentOrderType.ChargingCardOrder:
        this.chargingCardOrderStore.updateStoreState({ lastOrderID: idStr });
        await this.navigateToStatusRoute(CHARGING_CARD_ORDER_ROUTE, CHARGING_CARD_ORDER_STATUS_ROUTE);
        break;
      case RecentOrderType.PaymentAgreement:
        this.findPaymentSolutionStateService.lastOrderId = idStr;
        await this.navigateToStatusRoute(FIND_PAYMENT_SOLUTION_ROUTE, FIND_PAYMENT_SOLUTION_STATUS_ROUTE);
        break;
      case RecentOrderType.PartialAmountChange:
        this.partialPaymentStateService.lastOrderId = idStr;
        await this.navigateToStatusRoute(INVOICE_PARTIALPAYMENT_CHANGE_RATE_ROUTE, INVOICE_PARTIALPAYMENT_CHANGE_STATUS_ROUTE);
        break;
      case RecentOrderType.CAInformationRequest:
        this.caInformationRequestStateService.lastOrderId = idStr;
        await this.navigateToStatusRoute(CLEARING_ACCOUNT_INFORMATION_REQUEST_ROUTE, CLEARING_ACCOUNT_INFORMATION_REQUEST_STATUS_ROUTE);
        break;
      case RecentOrderType.Relocation:
        this.relocationStore.updateStoreState({ lastOrderID: idStr });
        await this.navigateToStatusRoute(RELOCATION_ROUTE, RELOCATION_STATUS_ROUTE);
        break;
      case RecentOrderType.MoveOut:
        await this.handleMoveOut(idStr, idNumber, dataTypeLower);
        break;
    }
  }

  async navigateToInvoiceDataChangeStatus(dataType: DataChangeType) {
    switch (dataType) {
      case DataChangeType.PdfInvoiceChangeOnly:
        await this.navigateToStatusRoute(PDF_INVOICE_CHANGE_ROUTE, PDF_INVOICE_CHANGE_STATUS_ROUTE);
        break;
      case DataChangeType.InvoiceAddressChangeOnly:
        await this.navigateToStatusRoute(INVOICE_ADDRESS_CHANGE_ROUTE, INVOICE_ADDRESS_CHANGE_STATUS_ROUTE);
        break;
      case DataChangeType.BankAccountChangeOnly:
      default:
        await this.navigateToStatusRoute(SEPA_INVOICE_CHANGE_ROUTE, SEPA_INVOICE_CHANGE_STATUS_ROUTE);
        break;
    }
  }

  private async handleTariffChange(order: RecentOrder, idStr: string, idNumber: number, dataTypeLower: string) {
    if (dataTypeLower.includes('tc_emob')) {
      this.emobTariffChangeStore.updateStoreState({ submitId: idNumber });
      await this.navigateToStatusRoute(TARIFF_CHANGE_EMOBILITY_ROUTE, TARIFF_CHANGE_STATUS_ROUTE);
      return;
    }

    this.tariffChangeService.setLastOrderId(idStr);

    if (order.dataType === DataChangeType.SecureOffer) {
      await this.navigateToStatusRoute(SECURE_OFFER_ROUTE, SECURE_OFFER_STATUS_ROUTE);
      return;
    }

    if (order.dataType === DataChangeType.TariffChangeMAC) {
      await this.navigateToStatusRoute(OFFER_CONFIRMATION_ROUTE, OFFER_CONFIRMATION_STATUS_ROUTE);
      return;
    }

    // V1 -> TariffChangeStateService, V2 -> TariffChangeStore, selected by provideTariffChangeLogic.
    this.tariffChangeLogic.setLastOrderId(idStr);
    await this.navigateToStatusRoute(TARIFF_CHANGE_ROUTE, TARIFF_CHANGE_STATUS_ROUTE);
  }

  private async handleNewClient(order: RecentOrder, idStr: string) {
    const isInternetV2Enabled = this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.INTERNET_V2_ENABLED);
    const isNewClientV2Enabled = this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.NEW_CLIENT_V2_ENABLED);

    if ((order.dataType === DataChangeType.InternetOrder || order.dataType === DataChangeType.NewClientIO) && isInternetV2Enabled) {
      this.internetStore.updateStoreState({ submitId: order.id, orderType: order.type });
      await this.navigateToStatusRoute(INTERNET_ROUTE, NEW_CLIENT_STATUS_ROUTE);
      return;
    }

    if (isNewClientV2Enabled) {
      this.orderService.setLastOrderidV2(idStr);
    } else {
      this.orderService.setLastOrderId(idStr);
    }

    if (order.dataType === DataChangeType.NewClientIO) {
      this.orderService.setLastOrderId(idStr);
      this.orderService.setNewClientType('superschnell-bestellen');
      await this.navigateToStatusRoute(SUPERSCHNELL_ROUTE, NEW_CLIENT_STATUS_ROUTE);
      return;
    }

    this.orderService.setNewClientType('kunde-werden');
    if (order.dataType === DataChangeType.NewClientMAC) {
      await this.navigateToStatusRoute(OFFER_CONFIRMATION_ROUTE, OFFER_CONFIRMATION_STATUS_ROUTE);
      return;
    }

    await this.navigateToStatusRoute(NEW_CLIENT_ROUTE, NEW_CLIENT_STATUS_ROUTE);
  }

  private async handleMoveOut(idStr: string, idNumber: number, dataTypeLower: string) {
    if (dataTypeLower.includes('emobility')) {
      this.emobCancellationStore.updateStore({ submitId: idNumber });
      await this.navigateToStatusRoute(EMOB_CANCELLATION_ROUTE, EMOB_CANCELLATION_STATUS_ROUTE);
      return;
    }

    this.moveOutStateService.lastOrderId = idStr;
    await this.navigateToStatusRoute(MOVE_OUT_ROUTE, MOVE_OUT_STATUS_ROUTE);
  }

  private async navigateToStatusRoute(route: string, statusRoute: string) {
    await this.router.navigate([`/${route}/${statusRoute}`]);
  }
}
